import { useServices, useToast } from '@/app/providers';
import { Button, Card } from '@/core/ui/components';
import type { HikCentralImportPreviewResponse, HikCentralImportResult } from '@yawi/types';
import { Send } from 'lucide-react';
import { useCallback, useReducer } from 'react';
import { HikCentralFileUpload } from './HikCentralFileUpload';
import { HikCentralImportResults } from './HikCentralImportResults';
import { HikCentralPreviewTable } from './HikCentralPreviewTable';
import { PreviewCountdown } from './PreviewCountdown';

type Phase = 'upload' | 'review' | 'result';

interface Assignment {
  memberId: string;
  displayName: string;
}

interface State {
  phase: Phase;
  isLoading: boolean;
  selectedFile: File | null;
  uploadError: string | null;
  preview: HikCentralImportPreviewResponse | null;
  assignments: Record<number, Assignment>;
  expired: boolean;
  result: HikCentralImportResult | null;
}

type Action =
  | { type: 'SELECT_FILE'; file: File }
  | { type: 'CLEAR_FILE' }
  | { type: 'PREVIEW_START' }
  | { type: 'PREVIEW_SUCCESS'; preview: HikCentralImportPreviewResponse }
  | { type: 'PREVIEW_ERROR'; error: string }
  | { type: 'ASSIGN'; rowIndex: number; memberId: string; displayName: string }
  | { type: 'EXPIRE' }
  | { type: 'APPLY_START' }
  | { type: 'APPLY_SUCCESS'; result: HikCentralImportResult }
  | { type: 'APPLY_ERROR' }
  | { type: 'RESET' };

function buildInitialAssignments(preview: HikCentralImportPreviewResponse): Record<number, Assignment> {
  const map: Record<number, Assignment> = {};
  for (const row of preview.rows) {
    if (row.autoSelect) {
      const suggestion = row.suggestions.find((s) => s.memberId === row.autoSelect);
      map[row.rowIndex] = {
        memberId: row.autoSelect,
        displayName: suggestion?.displayName ?? '',
      };
    }
  }
  return map;
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SELECT_FILE': return { ...state, selectedFile: action.file, uploadError: null };
    case 'CLEAR_FILE': return { ...state, selectedFile: null, uploadError: null };
    case 'PREVIEW_START': return { ...state, isLoading: true, uploadError: null };
    case 'PREVIEW_SUCCESS': return {
      ...state,
      isLoading: false,
      phase: 'review',
      preview: action.preview,
      assignments: buildInitialAssignments(action.preview),
      expired: false,
    };
    case 'PREVIEW_ERROR': return { ...state, isLoading: false, uploadError: action.error };
    case 'ASSIGN': {
      const next = { ...state.assignments };
      if (action.memberId) {
        next[action.rowIndex] = { memberId: action.memberId, displayName: action.displayName };
      } else {
        delete next[action.rowIndex];
      }
      return { ...state, assignments: next };
    }
    case 'EXPIRE': return { ...state, expired: true };
    case 'APPLY_START': return { ...state, isLoading: true };
    case 'APPLY_SUCCESS': return { ...state, isLoading: false, phase: 'result', result: action.result };
    case 'APPLY_ERROR': return { ...state, isLoading: false };
    case 'RESET': return { ...initialState };
    default: return state;
  }
}

const initialState: State = {
  phase: 'upload',
  isLoading: false,
  selectedFile: null,
  uploadError: null,
  preview: null,
  assignments: {},
  expired: false,
  result: null,
};

interface HikCentralImportTabProps {
  tenantId: string;
}

export function HikCentralImportTab({ tenantId }: HikCentralImportTabProps) {
  const { hikCentralImportRepository } = useServices();
  const { showToast } = useToast();
  const [state, dispatch] = useReducer(reducer, initialState);

  const handlePreview = async () => {
    if (!state.selectedFile) return;
    dispatch({ type: 'PREVIEW_START' });
    try {
      const preview = await hikCentralImportRepository.preview(tenantId, state.selectedFile);
      dispatch({ type: 'PREVIEW_SUCCESS', preview });
      if (preview.totalRows > 500) {
        showToast('warning', `El archivo tiene ${preview.totalRows} filas. Solo se procesarán las primeras 500.`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al procesar el archivo';
      const lower = msg.toLowerCase();
      const display = lower.includes('400') || lower.includes('invalid') || lower.includes('xlsx')
        ? 'Archivo inválido. Verifica que sea .xlsx exportado de HikCentral y no supere 5 MB.'
        : lower.includes('403')
        ? 'No tienes permisos para esta acción.'
        : lower.includes('404')
        ? 'Tenant no encontrado.'
        : msg;
      dispatch({ type: 'PREVIEW_ERROR', error: display });
    }
  };

  const handleApply = async () => {
    if (!state.preview) return;
    const assignments = Object.entries(state.assignments)
      .filter(([, a]) => a.memberId)
      .map(([rowIndex, a]) => ({ rowIndex: Number(rowIndex), memberId: a.memberId }));

    if (assignments.length === 0) {
      showToast('warning', 'No hay asignaciones para aplicar.');
      return;
    }

    dispatch({ type: 'APPLY_START' });
    try {
      const result = await hikCentralImportRepository.apply(tenantId, {
        previewId: state.preview.previewId,
        assignments,
      });
      dispatch({ type: 'APPLY_SUCCESS', result });
      showToast('success', `${result.applied} miembro${result.applied !== 1 ? 's' : ''} actualizado${result.applied !== 1 ? 's' : ''} correctamente.`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al aplicar';
      const lower = msg.toLowerCase();
      if (lower.includes('expired') || lower.includes('preview')) {
        showToast('error', 'La previsualización expiró. Sube el archivo de nuevo.');
        dispatch({ type: 'RESET' });
      } else {
        showToast('error', msg);
        dispatch({ type: 'APPLY_ERROR' });
      }
    }
  };

  const handleSearch = useCallback(
    (query: string) => hikCentralImportRepository.searchMembers(tenantId, query),
    [hikCentralImportRepository, tenantId],
  );

  const handleExpire = useCallback(() => dispatch({ type: 'EXPIRE' }), []);
  const assignedCount = Object.values(state.assignments).filter((a) => a.memberId).length;

  return (
    <div className="max-w-6xl space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-theme">Importar personas desde HikCentral</h3>
        <p className="text-sm text-theme-secondary mt-1">
          Sube el export "Person Information" de HikCentral (.xlsx). Yawi asignará el acsCode a cada miembro y creará sus credenciales RFID.
        </p>
      </div>

      {state.phase === 'upload' && (
        <Card>
          <div className="space-y-4">
            <HikCentralFileUpload
              selectedFile={state.selectedFile}
              onFileSelected={(file) => dispatch({ type: 'SELECT_FILE', file })}
              onClear={() => dispatch({ type: 'CLEAR_FILE' })}
              disabled={state.isLoading}
              error={state.uploadError ?? undefined}
            />
            <Button
              onClick={handlePreview}
              disabled={!state.selectedFile || state.isLoading}
              isLoading={state.isLoading}
            >
              Previsualizar
            </Button>
          </div>
        </Card>
      )}

      {state.phase === 'review' && state.preview && (
        <div className="space-y-4">
          <Card>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <p className="text-sm font-medium text-theme">
                {state.preview.totalRows} filas encontradas
              </p>
              <PreviewCountdown expiresAt={state.preview.expiresAt} onExpire={handleExpire} />
            </div>
          </Card>

          {state.expired && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              El preview expiró (30 min). Sube el archivo de nuevo para continuar.
            </div>
          )}

          <Card>
            <HikCentralPreviewTable
              rows={state.preview.rows}
              assignments={state.assignments}
              onAssignmentChange={(rowIndex, memberId, displayName) =>
                dispatch({ type: 'ASSIGN', rowIndex, memberId, displayName })
              }
              onSearch={handleSearch}
              disabled={state.isLoading || state.expired}
            />
          </Card>

          <div className="flex items-center gap-3">
            <Button
              onClick={handleApply}
              disabled={state.isLoading || state.expired || assignedCount === 0}
              isLoading={state.isLoading}
              leftIcon={<Send className="w-4 h-4" />}
            >
              Aplicar {assignedCount > 0 ? `(${assignedCount})` : ''}
            </Button>
            <Button variant="outline" onClick={() => dispatch({ type: 'RESET' })} disabled={state.isLoading}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {state.phase === 'result' && state.result && (
        <Card>
          <HikCentralImportResults result={state.result} onReset={() => dispatch({ type: 'RESET' })} />
        </Card>
      )}
    </div>
  );
}
